import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Chip,
  Fade,
  Grow,
  useScrollTrigger,
  Paper,
  Divider,
  Link,
  Stack,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Email,
  WhatsApp,
  LocationOn,
  LinkedIn,
  Code,
  Storage,
  School,
  Download,
  WorkspacePremium,
  Verified,
  AutoAwesome,
  BusinessCenter,
  CalendarToday,
  Psychology,
} from '@mui/icons-material';

const theme = createTheme({
  palette: {
    primary: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    secondary: {
      main: '#111827',
      light: '#374151',
      dark: '#000000',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          padding: '10px 24px',
        },
      },
    },
  },
});

export default function Portfolio() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [imageError, setImageError] = useState(false);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'skills', 'projects', 'education', 'experience'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMenuOpen(false);
  };

  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/SoujanyaCV.pdf';
    link.download = 'SoujanyaCV.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const skills = {
    programming: ['Java', 'C#', 'C'],
    databases: ['MySQL'],
    soft: ['Adaptability', 'Leadership Skills', 'Communication Skills', 'Effective Time Management'],
  };

  const projects = [
    {
      title: 'Deep Learning-Based Detection of Helicobacter pylori',
      description: 'Advanced AI-based diagnostic system using deep learning algorithms for medical data analysis to enhance the accuracy of detecting H. pylori infections.',
      tags: ['Deep Learning', 'Medical AI', 'Diagnostics'],
      year: '2024',
    },
    {
      title: 'VacciBot',
      description: 'AI-powered web-based chatbot funded by KSCST. Addresses vaccination queries for pregnant women, children, and infants. Contributed as Frontend Developer.',
      tags: ['AI Chatbot', 'Frontend Development', 'Government Funded'],
      year: '2023-24',
    },
  ];

  const education = [
    {
      degree: 'Master of Science in Software Technology',
      institution: 'AIMIT, St. Aloysius College (Autonomous), Mangalore',
      year: '2023 - 2025',
      grade: '8.41',
      level: 'Postgraduate',
    },
    {
      degree: 'Bachelor of Science (PMCs)',
      institution: 'Dr. P. Dayananda Pai - P. Sathisha Pai Government First Grade College, Mangalore',
      year: '2020 - 2023',
      grade: '8.14',
      level: 'Undergraduate',
    },
    {
      degree: 'PUC (12th)',
      institution: 'Govt. PU College for Girls, Balmatta, Mangalore',
      year: '2020',
      grade: '73.33%',
      level: 'Pre-University',
    },
  ];

  const menuItems = ['Home', 'About', 'Skills', 'Projects', 'Education', 'Experience'];

  const socialLinks = {
    email: 'mailto:soujanyaganiga9@gmail.com',
    linkedin: 'https://www.linkedin.com/in/soujanya-ganiga',
    github: 'https://github.com/soujanyaganiga',
    website: 'https://soujanyaganiga.in',
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Navigation */}
        <AppBar
          position="fixed"
          elevation={trigger ? 4 : 2}
          sx={{
            bgcolor: trigger ? 'rgba(255, 255, 255, 0.98)' : 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            transition: 'all 0.3s ease',
            borderBottom: trigger ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(245, 158, 11, 0.2)',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {!imageError ? (
                  <Avatar
                    src="/profile-photo.jpeg"
                    alt="Soujanya"
                    sx={{
                      width: 40,
                      height: 40,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      boxShadow: '0 0 10px rgba(245, 158, 11, 0.3)',
                      cursor: 'pointer',
                    }}
                    onClick={() => scrollToSection('home')}
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      border: '2px solid',
                      borderColor: 'primary.main',
                      cursor: 'pointer',
                    }}
                    onClick={() => scrollToSection('home')}
                  >
                    S
                  </Avatar>
                )}

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: trigger ? 'secondary.main' : 'white',
                    cursor: 'pointer',
                  }}
                  onClick={() => scrollToSection('home')}
                >
                  Soujanya<Box component="span" sx={{ color: 'primary.main' }}>.</Box>
                </Typography>
              </Box>

              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    sx={{
                      color: activeSection === item.toLowerCase()
                        ? 'primary.main'
                        : trigger ? 'secondary.main' : 'white',
                      bgcolor: activeSection === item.toLowerCase()
                        ? 'rgba(245, 158, 11, 0.15)'
                        : 'transparent',
                      fontWeight: activeSection === item.toLowerCase() ? 700 : 500,
                      '&:hover': {
                        bgcolor: 'rgba(245, 158, 11, 0.15)',
                        color: 'primary.main',
                      },
                    }}
                  >
                    {item}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Download />}
                  onClick={handleDownloadCV}
                  sx={{
                    ml: 2,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  CV
                </Button>
              </Box>

              <IconButton
                sx={{
                  display: { xs: 'block', md: 'none' },
                  color: trigger ? 'secondary.main' : 'white',
                  border: '1px solid',
                  borderColor: trigger ? 'secondary.main' : 'white',
                }}
                onClick={() => setMenuOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>

        <Drawer
          anchor="right"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
        >
          <Box sx={{ width: 280, pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mb: 2 }}>
              {!imageError ? (
                <Avatar
                  src="/profile-photo.jpeg"
                  alt="Soujanya"
                  sx={{ width: 48, height: 48 }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>S</Avatar>
              )}
              <IconButton onClick={() => setMenuOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <List>
              {menuItems.map((item) => (
                <ListItem key={item} disablePadding>
                  <ListItemButton
                    onClick={() => scrollToSection(item.toLowerCase())}
                    selected={activeSection === item.toLowerCase()}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: 'rgba(245, 158, 11, 0.15)',
                        color: 'primary.main',
                        fontWeight: 700,
                        '&:hover': {
                          bgcolor: 'rgba(245, 158, 11, 0.25)',
                        },
                      },
                    }}
                  >
                    <ListItemText primary={item} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Box sx={{ px: 2, mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<Download />}
                onClick={handleDownloadCV}
              >
                Download CV
              </Button>
            </Box>
          </Box>
        </Drawer>

        {/* Hero Section - UNCHANGED */}
        <Box
          id="home"
          sx={{
            pt: { xs: 14, md: 18 },
            pb: { xs: 10, md: 14 },
            background: 'linear-gradient(135deg, #ffffff 0%, #fef3c7 50%, #ffffff 100%)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <Fade in timeout={800}>
                  <Box>
                    <Chip
                      label="Welcome to my portfolio"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        mb: 3,
                        fontSize: '0.9rem',
                        px: 2,
                        py: 3,
                      }}
                    />
                    <Typography variant="h1" sx={{ mb: 2 }}>
                      <Box component="span" sx={{ color: 'secondary.main' }}>Soujanya</Box>
                      <br />
                      <Box component="span" sx={{ color: 'primary.main' }}>Ganiga</Box>
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'secondary.light', mb: 1, fontWeight: 600 }}>
                      Assistant Professor
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4, color: 'text.secondary' }}>
                      <LocationOn sx={{ color: 'primary.main' }} />
                      <Typography variant="body1" fontWeight={500}>Manjeshwara, Kerala</Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 4,
                        lineHeight: 1.8,
                        maxWidth: 600,
                        color: 'text.secondary',
                        fontSize: '1.05rem',
                      }}
                    >
                      Assistant Professor of Computer Applications with a passion for teaching, research, and academic excellence. Dedicated to empowering students with strong foundations in computing, innovation, and real-world problem solving.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        onClick={() => scrollToSection('projects')}
                        sx={{
                          boxShadow: 3,
                          px: 4,
                          py: 1.5,
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        View My Work
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        onClick={() => scrollToSection('about')}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        About Me
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <IconButton
                        component={Link}
                        href={socialLinks.email}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: 'background.paper',
                          boxShadow: 2,
                          width: 48,
                          height: 48,
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Email />
                      </IconButton>
                      <IconButton
                        component={Link}
                        href={socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: 'background.paper',
                          boxShadow: 2,
                          width: 48,
                          height: 48,
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <LinkedIn />
                      </IconButton>
                      {/* <IconButton
                        component={Link}
                        href={socialLinks.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: 'background.paper',
                          boxShadow: 2,
                          width: 48,
                          height: 48,
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <GitHub />
                      </IconButton> */}
                    </Box>
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={5}>
                <Grow in timeout={1000}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
                    <Box
                      sx={{
                        width: { xs: 280, sm: 350 },
                        height: { xs: 280, sm: 350 },
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 20px 60px rgba(245, 158, 11, 0.4)',
                        animation: 'float 6s ease-in-out infinite',
                        position: 'relative',
                        overflow: 'hidden',
                        '@keyframes float': {
                          '0%, 100%': {
                            transform: 'translateY(0)',
                          },
                          '50%': {
                            transform: 'translateY(-20px)',
                          },
                        },
                      }}
                    >
                      {!imageError ? (
                        <Box
                          sx={{
                            width: { xs: 240, sm: 300 },
                            height: { xs: 240, sm: 300 },
                            borderRadius: '50%',
                            overflow: 'hidden',
                            bgcolor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                          }}
                        >
                          <img
                            src="/profile-photo.jpeg"
                            alt="Soujanya"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={() => setImageError(true)}
                          />
                        </Box>
                      ) : (
                        <Avatar
                          sx={{
                            width: { xs: 240, sm: 300 },
                            height: { xs: 240, sm: 300 },
                            bgcolor: 'white',
                            fontSize: '8rem',
                            color: 'primary.main',
                            fontWeight: 700,
                          }}
                        >
                          S
                        </Avatar>
                      )}
                    </Box>
                    {/* <Avatar
                      sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: { xs: 20, sm: 60 },
                        width: 80,
                        height: 80,
                        bgcolor: 'secondary.main',
                        boxShadow: 4,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': {
                            transform: 'scale(1)',
                          },
                          '50%': {
                            transform: 'scale(1.05)',
                          },
                        },
                      }}
                    >
                      <Code sx={{ fontSize: 40 }} />
                    </Avatar> */}
                  </Box>
                </Grow>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* About Section - REDESIGNED WITH SHAPES */}
        <Box id="about" sx={{ py: 8, bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
          {/* Animated Background Shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -80,
              width: 350,
              height: 350,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)',
              animation: 'float 8s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px) translateX(0px)' },
                '50%': { transform: 'translateY(-30px) translateX(20px)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -100,
              left: -100,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.1) 0%, transparent 70%)',
              animation: 'float 10s ease-in-out infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '30%',
              right: '5%',
              width: 150,
              height: 150,
              border: '3px solid',
              borderColor: 'rgba(245, 158, 11, 0.2)',
              borderRadius: '30px',
              transform: 'rotate(45deg)',
              animation: 'spin 20s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(45deg)' },
                '100%': { transform: 'rotate(405deg)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              left: '10%',
              width: 200,
              height: 200,
              borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
              background: 'rgba(245, 158, 11, 0.08)',
              animation: 'morph 8s ease-in-out infinite',
              '@keyframes morph': {
                '0%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
                '50%': { borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%' },
                '100%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: '15%',
              width: 100,
              height: 100,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(245, 158, 11, 0.25)',
              animation: 'pulse-ring 3s ease-out infinite',
              '@keyframes pulse-ring': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '100%': {
                  transform: 'scale(1.8)',
                  opacity: 0,
                },
              },
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="GET TO KNOW ME"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 3 }}>
                About <Box component="span" sx={{ color: 'primary.main' }}>Me</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.7 }}>
                Experienced teaching professional in Computer Applications, focused on delivering concept-driven instruction, guiding students academically and professionally, and promoting disciplined classroom engagement. Committed to lifelong learning and empowering students to excel in technology-driven careers.
              </Typography>
            </Box>

            <Grid container spacing={4} justifyContent="center">
              {/* EMAIL CARD */}
              <Grid item xs={12} md={4}>
                <Grow in timeout={600}>
                  <Card
                    component={Link}
                    href={socialLinks.email}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      height: '100%',
                      background: 'white',
                      border: '1px solid #E0E0E0',
                      borderRadius: '32px',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: 'translateY(-16px)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
                        borderColor: 'rgba(234, 67, 53, 0.4)',
                        '& .email-icon-box': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 16px 40px rgba(234, 67, 53, 0.3)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 5, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                      <Box
                        className="email-icon-box"
                        sx={{
                          width: 90,
                          height: 90,
                          borderRadius: '32px',
                          background: 'linear-gradient(135deg, #EA4335 0%, #c5221f 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          transition: 'all 0.4s ease',
                          boxShadow: '0 12px 40px rgba(234, 67, 53, 0.3)',
                        }}
                      >
                        <Email sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          color: '#1a1a1a',
                          fontSize: '1.25rem',
                        }}
                      >
                        Email Address
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: '#666666',
                          lineHeight: 1.7,
                        }}
                      >
                        soujanyaganiga9@gmail.com
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>

              {/* PHONE CARD */}
              {/* <Grid item xs={12} md={4}>
                <Grow in timeout={750}>
                  <Card
                    component={Link}
                    href="tel:+919061752785"
                    sx={{
                      height: '100%',
                      background: 'white',
                      border: '1px solid #E0E0E0',
                      borderRadius: '32px',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: 'translateY(-16px)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
                        borderColor: 'rgba(52, 168, 83, 0.4)',
                        '& .phone-icon-box': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 16px 40px rgba(52, 168, 83, 0.3)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 5, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                      <Box
                        className="phone-icon-box"
                        sx={{
                          width: 90,
                          height: 90,
                          borderRadius: '32px',
                          background: 'linear-gradient(135deg, #34A853 0%, #2d8659 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          transition: 'all 0.4s ease',
                          boxShadow: '0 12px 40px rgba(52, 168, 83, 0.3)',
                        }}
                      >
                        <Phone sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          color: '#1a1a1a',
                          fontSize: '1.25rem',
                        }}
                      >
                        Phone Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: '#666666',
                          lineHeight: 1.7,
                        }}
                      >
                        +91 9061752785
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid> */}

              {/* LOCATION CARD */}
              <Grid item xs={12} md={4}>
                <Grow in timeout={900}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'white',
                      border: '1px solid #E0E0E0',
                      borderRadius: '32px',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: 'translateY(-16px)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
                        borderColor: 'rgba(66, 133, 244, 0.4)',
                        '& .location-icon-box': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 16px 40px rgba(66, 133, 244, 0.3)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 5, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                      <Box
                        className="location-icon-box"
                        sx={{
                          width: 90,
                          height: 90,
                          borderRadius: '32px',
                          background: 'linear-gradient(135deg, #4285F4 0%, #1f70c4 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          transition: 'all 0.4s ease',
                          boxShadow: '0 12px 40px rgba(66, 133, 244, 0.3)',
                        }}
                      >
                        <LocationOn sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          color: '#1a1a1a',
                          fontSize: '1.25rem',
                        }}
                      >
                        Current Location
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: '#666666',
                          lineHeight: 1.7,
                        }}
                      >
                        Manjeshwara, Kerala, India
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Skills Section - REDESIGNED WITH SHAPES */}
        <Box id="skills" sx={{ py: 8, bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
          {/* Animated Background Shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              left: -100,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
              animation: 'float 10s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px) translateX(0px)' },
                '50%': { transform: 'translateY(-40px) translateX(-30px)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -80,
              right: -60,
              width: 350,
              height: 350,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(240, 147, 251, 0.12) 0%, transparent 70%)',
              animation: 'float 12s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '40%',
              right: '8%',
              width: 120,
              height: 120,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'rgba(102, 126, 234, 0.2)',
              animation: 'pulse-ring 4s ease-out infinite',
              '@keyframes pulse-ring': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '100%': {
                  transform: 'scale(2)',
                  opacity: 0,
                },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '25%',
              left: '5%',
              width: 180,
              height: 180,
              borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
              background: 'rgba(79, 172, 254, 0.08)',
              animation: 'morph 8s ease-in-out infinite',
              '@keyframes morph': {
                '0%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
                '50%': { borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%' },
                '100%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              left: '15%',
              width: 140,
              height: 140,
              border: '2px solid',
              borderColor: 'rgba(245, 87, 108, 0.15)',
              borderRadius: '25px',
              transform: 'rotate(15deg)',
              animation: 'spin 25s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(15deg)' },
                '100%': { transform: 'rotate(375deg)' },
              },
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="MY EXPERTISE"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 3 }}>
                Skills & <Box component="span" sx={{ color: 'primary.main' }}>Competencies</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.7 }}>
                A comprehensive overview of my technical abilities and professional strengths
              </Typography>
            </Box>

            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  title: 'Programming Languages',
                  icon: Code,
                  items: skills.programming,
                  gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  iconColor: '#667eea',
                },
                {
                  title: 'Database Management',
                  icon: Storage,
                  items: skills.databases,
                  gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  iconColor: '#f093fb',
                },
                {
                  title: 'Professional Skills',
                  icon: Psychology,
                  items: skills.soft,
                  gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  iconColor: '#4facfe',
                },
              ].map((category, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Grow in timeout={600 + index * 150}>
                    <Card
                      sx={{
                        height: '100%',
                        background: 'white',
                        border: '1px solid',
                        borderColor: '#E0E0E0',
                        borderRadius: '24px',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-16px)',
                          boxShadow: '0 24px 50px rgba(0,0,0,0.15)',
                          borderColor: category.iconColor + '40',
                          '& .skill-icon-wrapper': {
                            transform: 'scale(1.2) rotate(10deg)',
                            boxShadow: `0 20px 50px ${category.iconColor}35`,
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ p: 5 }}>
                        <Box
                          className="skill-icon-wrapper"
                          sx={{
                            width: 90,
                            height: 90,
                            borderRadius: '24px',
                            background: category.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 4,
                            boxShadow: `0 12px 40px ${category.iconColor}30`,
                            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            position: 'relative',
                          }}
                        >
                          <category.icon sx={{ fontSize: 44, color: 'white' }} />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 3,
                            fontSize: '1.2rem',
                            color: '#1a1a1a',
                          }}
                        >
                          {category.title}
                        </Typography>
                        <Stack spacing={1.2}>
                          {category.items.map((skill, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.2,
                                borderRadius: '12px',
                                transition: 'all 0.3s ease',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                  bgcolor: 'rgba(245, 158, 11, 0.08)',
                                  transform: 'translateX(8px)',
                                },
                              }}
                            >
                              <Verified sx={{ fontSize: 20, color: category.iconColor, flexShrink: 0 }} />
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  flex: 1,
                                  color: '#333',
                                }}
                              >
                                {skill}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Projects Section - REDESIGNED WITH SHAPES */}
        <Box id="projects" sx={{ py: 8, bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)', position: 'relative', overflow: 'hidden' }}>
          {/* Animated Background Shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -80,
              width: 420,
              height: 420,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.12) 0%, transparent 70%)',
              animation: 'float 9s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-40px)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -120,
              left: -100,
              width: 380,
              height: 380,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.1) 0%, transparent 70%)',
              animation: 'float 11s ease-in-out infinite',
              animationDelay: '1.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '35%',
              right: '10%',
              width: 160,
              height: 160,
              borderRadius: '40px',
              border: '3px dashed',
              borderColor: 'rgba(245, 158, 11, 0.15)',
              animation: 'spin 30s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              left: '8%',
              width: 200,
              height: 200,
              borderRadius: '50% 40% 50% 40% / 40% 50% 40% 50%',
              background: 'rgba(245, 158, 11, 0.06)',
              animation: 'morph 7s ease-in-out infinite',
              '@keyframes morph': {
                '0%': { borderRadius: '50% 40% 50% 40% / 40% 50% 40% 50%' },
                '50%': { borderRadius: '40% 50% 40% 50% / 50% 40% 50% 40%' },
                '100%': { borderRadius: '50% 40% 50% 40% / 40% 50% 40% 50%' },
              },
            }}
          />

          {/* <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="PORTFOLIO"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.15)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 3 }}>
                Featured <Box component="span" sx={{ color: 'primary.main' }}>Projects</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.7 }}>
                Innovative solutions combining technology and research
              </Typography>
            </Box>

            <Stack spacing={4}>
              {projects.map((project, index) => (
                <Grow in timeout={600 + index * 200} key={index}>
                  <Card
                    sx={{
                      background: 'white',
                      border: '1px solid',
                      borderColor: '#E5E7EB',
                      borderRadius: '20px',
                      boxShadow: '0 10px 35px rgba(0,0,0,0.09)',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        background: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)',
                        transform: 'scaleY(0)',
                        transformOrigin: 'bottom',
                        transition: 'transform 0.5s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-12px)',
                        boxShadow: '0 25px 50px rgba(245, 158, 11, 0.18)',
                        borderColor: 'rgba(245, 158, 11, 0.3)',
                        '&::before': {
                          transform: 'scaleY(1)',
                        },
                        '& .project-number': {
                          transform: 'scale(1.15)',
                          boxShadow: '0 15px 45px rgba(245, 158, 11, 0.4)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 6 }}>
                      <Grid container spacing={4} alignItems="flex-start">
                        <Grid item xs={12} sm="auto">
                          <Box
                            className="project-number"
                            sx={{
                              width: 70,
                              height: 70,
                              borderRadius: '16px',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.8rem',
                              fontWeight: 800,
                              color: 'white',
                              boxShadow: '0 10px 30px rgba(245, 158, 11, 0.3)',
                              transition: 'all 0.4s ease',
                            }}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm>
                          <Box sx={{ mb: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                mb: 2,
                                color: '#1a1a1a',
                                fontSize: '1.4rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {project.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                              <Chip
                                icon={<CalendarToday sx={{ fontSize: 16 }} />}
                                label={project.year}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                                  color: 'primary.dark',
                                  fontWeight: 700,
                                  border: '1.5px solid',
                                  borderColor: 'primary.light',
                                  fontSize: '0.85rem',
                                }}
                              />
                            </Box>
                          </Box>

                          <Typography
                            variant="body1"
                            sx={{
                              mb: 4,
                              lineHeight: 1.9,
                              color: '#555555',
                              fontSize: '0.98rem',
                            }}
                          >
                            {project.description}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {project.tags.map((tag, idx) => (
                              <Chip
                                key={idx}
                                icon={<AutoAwesome sx={{ fontSize: 14 }} />}
                                label={tag}
                                sx={{
                                  bgcolor: '#F3F4F6',
                                  color: '#374151',
                                  fontWeight: 600,
                                  border: '1px solid #E5E7EB',
                                  fontSize: '0.85rem',
                                  padding: '4px 2px',
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grow>
              ))}
            </Stack>
          </Container> */}
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="PORTFOLIO"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 2 }}>
                Featured <Box component="span" sx={{ color: 'primary.main' }}>Projects</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontSize: '1rem', lineHeight: 1.7 }}>
                Innovative solutions combining technology and research
              </Typography>
            </Box>

            <Stack spacing={3}>
              {projects.map((project, index) => (
                <Grow in timeout={600 + index * 200} key={index}>
                  <Card
                    sx={{
                      background: 'white',
                      border: '2px solid',
                      borderColor: 'rgba(245, 158, 11, 0.15)',
                      borderRadius: '18px',
                      boxShadow: '0 6px 25px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        background: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)',
                        transform: 'scaleY(0)',
                        transformOrigin: 'bottom',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(245, 158, 11, 0.15)',
                        borderColor: 'primary.main',
                        '&::before': {
                          transform: 'scaleY(1)',
                        },
                        '& .project-number': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 10px 30px rgba(245, 158, 11, 0.35)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Grid container spacing={2.5} alignItems="flex-start">
                        <Grid item xs={12} sm="auto">
                          <Box
                            className="project-number"
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '14px',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.4rem',
                              fontWeight: 800,
                              color: 'white',
                              boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)',
                              transition: 'all 0.3s ease',
                              mx: { xs: 'auto', sm: 0 },
                            }}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                color: 'secondary.main',
                                fontSize: '1.15rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {project.title}
                            </Typography>
                            <Chip
                              icon={<CalendarToday sx={{ fontSize: 14 }} />}
                              label={project.year}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(245, 158, 11, 0.1)',
                                color: 'primary.dark',
                                fontWeight: 600,
                                border: '1.5px solid',
                                borderColor: 'rgba(245, 158, 11, 0.25)',
                                fontSize: '0.75rem',
                                height: 28,
                              }}
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2.5,
                              lineHeight: 1.7,
                              color: 'text.secondary',
                              fontSize: '0.9rem',
                            }}
                          >
                            {project.description}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {project.tags.map((tag, idx) => (
                              <Chip
                                key={idx}
                                icon={<AutoAwesome sx={{ fontSize: 12 }} />}
                                label={tag}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(245, 158, 11, 0.08)',
                                  color: 'text.primary',
                                  fontWeight: 600,
                                  border: '1px solid',
                                  borderColor: 'rgba(245, 158, 11, 0.2)',
                                  fontSize: '0.75rem',
                                  height: 28,
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 15px rgba(245, 158, 11, 0.25)',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grow>
              ))}
            </Stack>
          </Container>

        </Box>
        {/* Achievements & Certifications Section - REDESIGNED */}
        <Box id="achievements" sx={{ py: 8, bgcolor: 'linear-gradient(135deg, #fff5e6 0%, #fef3c7 100%)', position: 'relative', overflow: 'hidden' }}>
          {/* Animated Background Shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              left: -60,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
              animation: 'float 10s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-50px)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -100,
              right: -80,
              width: 350,
              height: 350,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.12) 0%, transparent 70%)',
              animation: 'float 12s ease-in-out infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '40%',
              left: '8%',
              width: 150,
              height: 150,
              borderRadius: '30px',
              border: '3px dashed',
              borderColor: 'rgba(245, 158, 11, 0.2)',
              animation: 'spin 25s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '15%',
              right: '12%',
              width: 180,
              height: 180,
              borderRadius: '50% 40% 50% 40% / 40% 50% 40% 50%',
              background: 'rgba(245, 158, 11, 0.08)',
              animation: 'morph 8s ease-in-out infinite',
              '@keyframes morph': {
                '0%': { borderRadius: '50% 40% 50% 40% / 40% 50% 40% 50%' },
                '50%': { borderRadius: '40% 50% 40% 50% / 50% 40% 50% 40%' },
                '100%': { borderRadius: '50% 40% 50% 40% / 40% 50% 40% 50%' },
              },
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="ACHIEVEMENTS"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.2)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 3 }}>
                Notable <Box component="span" sx={{ color: 'primary.main' }}>Achievements</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.7 }}>
                A few highlights from my academic and extracurricular contributions.
              </Typography>
            </Box>

            <Grow in timeout={700}>
              <Card
                sx={{
                  background: 'white',
                  border: '1px solid',
                  borderColor: '#E5E7EB',
                  borderRadius: '20px',
                  boxShadow: '0 10px 35px rgba(0,0,0,0.09)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 50px rgba(245, 158, 11, 0.15)',
                    borderColor: 'rgba(245, 158, 11, 0.2)',
                  },
                }}
              >
                <CardContent sx={{ p: 6 }}>
                  <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <ul style={{ margin: 0, paddingLeft: 22, lineHeight: 2 }}>
                        <li>
                          <Typography component="span" variant="body1" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                            Published a research chapter "VacciBot: Chatbot For Addressing Vaccination Query of Pregnant Women and Children"
                          </Typography>
                          <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            in SACAIM 2024, Vol. 2, December 2024. [DOI: 10.25215/9389476712]
                          </Typography>
                        </li>
                        <li>
                          <Typography component="span" variant="body1" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                            Successfully organized "Web Development using C#" online workshop
                          </Typography>
                          <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>for undergraduate students.</Typography>
                        </li>
                        <li>
                          <Typography component="span" variant="body1" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                            Successfully organized "Micro Teaching"
                          </Typography>
                          <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>for high school students.</Typography>
                        </li>
                      </ul>
                    </Box>

                    <Box sx={{ width: { xs: '100%', md: 300 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Paper elevation={0} sx={{ p: 4, borderRadius: 16, bgcolor: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(245, 158, 11, 0.04) 100%)', width: '100%', textAlign: 'center', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, fontSize: '1.1rem' }}>Certifications</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Verified certifications and course credentials.</Typography>
                        <Button
                          component="a"
                          href="https://drive.google.com/drive/folders/1vJoXm5o_BFVQhJMJFgnJ8QeUV97Y5KdO"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          color="primary"
                          startIcon={<WorkspacePremium />}
                          sx={{ width: '100%', boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 35px rgba(245, 158, 11, 0.4)' } }}
                        >
                          View Certifications
                        </Button>
                      </Paper>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grow>

          </Container>
        </Box>



        {/* Education Section - ROADMAP DESIGN WITH MORE SHAPES */}
        <Box id="education" sx={{ py: 10, bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
          {/* Animated Background Shapes - Enhanced */}

          {/* Large Circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -120,
              right: -120,
              width: 450,
              height: 450,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%)',
              animation: 'float 9s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px) translateX(0px)' },
                '50%': { transform: 'translateY(-35px) translateX(-25px)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -100,
              left: -100,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.08) 0%, transparent 70%)',
              animation: 'float 11s ease-in-out infinite',
              animationDelay: '1.5s',
            }}
          />

          {/* Medium Circles */}
          <Box
            sx={{
              position: 'absolute',
              top: '20%',
              left: '5%',
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)',
              animation: 'float 8s ease-in-out infinite',
              animationDelay: '0.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: '3%',
              width: 280,
              height: 280,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.1) 0%, transparent 70%)',
              animation: 'float 10s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />

          {/* Morphing Blobs */}
          <Box
            sx={{
              position: 'absolute',
              top: '35%',
              left: '8%',
              width: 180,
              height: 180,
              borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
              background: 'rgba(245, 158, 11, 0.06)',
              animation: 'morph 10s ease-in-out infinite',
              '@keyframes morph': {
                '0%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
                '50%': { borderRadius: '70% 30% 30% 70% / 60% 40% 60% 40%' },
                '100%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '15%',
              right: '12%',
              width: 200,
              height: 200,
              borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
              background: 'rgba(217, 119, 6, 0.05)',
              animation: 'morph 12s ease-in-out infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '70%',
              left: '15%',
              width: 150,
              height: 150,
              borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
              background: 'rgba(245, 158, 11, 0.07)',
              animation: 'morph 9s ease-in-out infinite',
              animationDelay: '2.5s',
            }}
          />

          {/* Rotating Squares */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '30%',
              right: '10%',
              width: 130,
              height: 130,
              border: '3px solid',
              borderColor: 'rgba(245, 158, 11, 0.15)',
              borderRadius: '35px',
              transform: 'rotate(25deg)',
              animation: 'spin 22s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(25deg)' },
                '100%': { transform: 'rotate(385deg)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              right: '18%',
              width: 100,
              height: 100,
              border: '2px solid',
              borderColor: 'rgba(217, 119, 6, 0.12)',
              borderRadius: '25px',
              transform: 'rotate(-20deg)',
              animation: 'spin 18s linear infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              left: '8%',
              width: 110,
              height: 110,
              border: '3px solid',
              borderColor: 'rgba(245, 158, 11, 0.18)',
              borderRadius: '28px',
              transform: 'rotate(15deg)',
              animation: 'spin 20s linear infinite',
              animationDelay: '3s',
            }}
          />

          {/* Pulsing Rings */}
          <Box
            sx={{
              position: 'absolute',
              top: '60%',
              right: '5%',
              width: 100,
              height: 100,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(245, 158, 11, 0.2)',
              animation: 'pulse-ring 3.5s ease-out infinite',
              '@keyframes pulse-ring': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '100%': {
                  transform: 'scale(2)',
                  opacity: 0,
                },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              left: '12%',
              width: 80,
              height: 80,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(217, 119, 6, 0.15)',
              animation: 'pulse-ring 4s ease-out infinite',
              animationDelay: '0.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '40%',
              right: '8%',
              width: 90,
              height: 90,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(245, 158, 11, 0.18)',
              animation: 'pulse-ring 3.8s ease-out infinite',
              animationDelay: '1.2s',
            }}
          />

          {/* Small Floating Rectangles */}
          <Box
            sx={{
              position: 'absolute',
              top: '15%',
              right: '25%',
              width: 70,
              height: 70,
              background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(217,119,6,0.08) 100%)',
              borderRadius: '18px',
              transform: 'rotate(-15deg)',
              animation: 'float 7s ease-in-out infinite',
              animationDelay: '0.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '35%',
              left: '20%',
              width: 60,
              height: 60,
              background: 'linear-gradient(135deg, rgba(217,119,6,0.1) 0%, rgba(245,158,11,0.05) 100%)',
              borderRadius: '15px',
              transform: 'rotate(20deg)',
              animation: 'float 6.5s ease-in-out infinite',
              animationDelay: '1.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '45%',
              right: '22%',
              width: 55,
              height: 55,
              background: 'linear-gradient(135deg, rgba(245,158,11,0.09) 0%, rgba(217,119,6,0.06) 100%)',
              borderRadius: '16px',
              transform: 'rotate(10deg)',
              animation: 'float 7.5s ease-in-out infinite',
              animationDelay: '2.2s',
            }}
          />

          {/* Tiny Dots */}
          <Box
            sx={{
              position: 'absolute',
              top: '12%',
              left: '25%',
              width: 15,
              height: 15,
              borderRadius: '50%',
              bgcolor: 'rgba(245, 158, 11, 0.3)',
              animation: 'float 5s ease-in-out infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '25%',
              right: '28%',
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'rgba(217, 119, 6, 0.25)',
              animation: 'float 6s ease-in-out infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '55%',
              left: '18%',
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'rgba(245, 158, 11, 0.28)',
              animation: 'float 5.5s ease-in-out infinite',
              animationDelay: '0.8s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '50%',
              right: '15%',
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: 'rgba(217, 119, 6, 0.22)',
              animation: 'float 6.2s ease-in-out infinite',
              animationDelay: '1.8s',
            }}
          />

          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="ACADEMIC BACKGROUND"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 2 }}>
                Educational <Box component="span" sx={{ color: 'primary.main' }}>Journey</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontSize: '1rem' }}>
                A path of continuous learning and academic excellence
              </Typography>
            </Box>

            {/* Timeline Container */}
            <Box sx={{ position: 'relative', pt: 2 }}>
              {/* Central Timeline Line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  background: 'linear-gradient(180deg, rgba(245,158,11,0.3) 0%, rgba(245,158,11,0.8) 50%, rgba(245,158,11,0.3) 100%)',
                  transform: 'translateX(-50%)',
                  zIndex: 0,
                }}
              />

              {education.map((edu, index) => (
                <Grow in timeout={500 + index * 200} key={index}>
                  <Box
                    sx={{
                      position: 'relative',
                      mb: index === education.length - 1 ? 0 : 6,
                    }}
                  >
                    {/* Timeline Dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: 30,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        border: '4px solid',
                        borderColor: 'background.paper',
                        boxShadow: '0 0 0 4px rgba(245,158,11,0.2)',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                        animation: 'pulse 2s ease-in-out infinite',
                        animationDelay: `${index * 0.3}s`,
                        '@keyframes pulse': {
                          '0%, 100%': {
                            boxShadow: '0 0 0 4px rgba(245,158,11,0.2)',
                          },
                          '50%': {
                            boxShadow: '0 0 0 8px rgba(245,158,11,0.1)',
                          },
                        },
                      }}
                    />

                    {/* Education Card */}
                    <Card
                      sx={{
                        maxWidth: 520,
                        mx: 'auto',
                        background: 'linear-gradient(135deg, #ffffff 0%, #fffbf5 100%)',
                        border: '2px solid',
                        borderColor: 'rgba(245, 158, 11, 0.2)',
                        borderRadius: '20px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                        },
                        '&:hover': {
                          transform: 'scale(1.03)',
                          boxShadow: '0 12px 35px rgba(245, 158, 11, 0.2)',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        {/* Header Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                          <Chip
                            icon={<School sx={{ fontSize: 16 }} />}
                            label={edu.level}
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              '& .MuiChip-icon': {
                                color: 'white',
                              },
                            }}
                          />

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              {edu.year}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Degree */}
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 2,
                            color: 'secondary.main',
                            fontSize: '1.1rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {edu.degree}
                        </Typography>

                        {/* Institution */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            mb: 2,
                            lineHeight: 1.6,
                            fontSize: '0.9rem',
                          }}
                        >
                          {edu.institution}
                        </Typography>

                        {/* Grade Badge */}
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            bgcolor: 'rgba(245, 158, 11, 0.1)',
                            borderRadius: '10px',
                            border: '1.5px solid',
                            borderColor: 'primary.main',
                          }}
                        >
                          <WorkspacePremium sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: 'primary.main',
                            }}
                          >
                            {edu.grade} {edu.grade.includes('%') ? '' : 'CGPA'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </Grow>
              ))}
            </Box>
          </Container>
        </Box>

        {/* Experience Section - REDESIGNED */}
        <Box id="experience" sx={{ py: 8, bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
          {/* Animated Background Shapes */}

          {/* Large Circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -150,
              left: -150,
              width: 500,
              height: 500,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)',
              animation: 'float 10s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px) translateX(0px)' },
                '50%': { transform: 'translateY(-40px) translateX(30px)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -120,
              right: -120,
              width: 450,
              height: 450,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.08) 0%, transparent 70%)',
              animation: 'float 12s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />

          {/* Medium Circles */}
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              right: '8%',
              width: 280,
              height: 280,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.09) 0%, transparent 70%)',
              animation: 'float 9s ease-in-out infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              left: '10%',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.07) 0%, transparent 70%)',
              animation: 'float 11s ease-in-out infinite',
              animationDelay: '1.5s',
            }}
          />

          {/* Morphing Blobs */}
          <Box
            sx={{
              position: 'absolute',
              top: '15%',
              left: '12%',
              width: 200,
              height: 200,
              borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
              background: 'rgba(245, 158, 11, 0.06)',
              animation: 'morph 11s ease-in-out infinite',
              '@keyframes morph': {
                '0%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
                '50%': { borderRadius: '70% 30% 30% 70% / 60% 40% 60% 40%' },
                '100%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '30%',
              right: '15%',
              width: 180,
              height: 180,
              borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
              background: 'rgba(217, 119, 6, 0.05)',
              animation: 'morph 13s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '60%',
              left: '18%',
              width: 160,
              height: 160,
              borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
              background: 'rgba(245, 158, 11, 0.07)',
              animation: 'morph 10s ease-in-out infinite',
              animationDelay: '1.2s',
            }}
          />

          {/* Rotating Squares */}
          <Box
            sx={{
              position: 'absolute',
              top: '20%',
              right: '20%',
              width: 140,
              height: 140,
              border: '3px solid',
              borderColor: 'rgba(245, 158, 11, 0.15)',
              borderRadius: '32px',
              transform: 'rotate(20deg)',
              animation: 'spin 24s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(20deg)' },
                '100%': { transform: 'rotate(380deg)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '25%',
              left: '15%',
              width: 120,
              height: 120,
              border: '2px solid',
              borderColor: 'rgba(217, 119, 6, 0.12)',
              borderRadius: '28px',
              transform: 'rotate(-25deg)',
              animation: 'spin 20s linear infinite',
              animationDelay: '1.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: '10%',
              width: 100,
              height: 100,
              border: '3px solid',
              borderColor: 'rgba(245, 158, 11, 0.18)',
              borderRadius: '24px',
              transform: 'rotate(15deg)',
              animation: 'spin 18s linear infinite',
              animationDelay: '2.5s',
            }}
          />

          {/* Pulsing Rings */}
          <Box
            sx={{
              position: 'absolute',
              top: '40%',
              left: '8%',
              width: 110,
              height: 110,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(245, 158, 11, 0.2)',
              animation: 'pulse-ring 4s ease-out infinite',
              '@keyframes pulse-ring': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '100%': {
                  transform: 'scale(2)',
                  opacity: 0,
                },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '35%',
              right: '12%',
              width: 95,
              height: 95,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(217, 119, 6, 0.18)',
              animation: 'pulse-ring 3.7s ease-out infinite',
              animationDelay: '0.8s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '65%',
              right: '25%',
              width: 85,
              height: 85,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(245, 158, 11, 0.16)',
              animation: 'pulse-ring 4.2s ease-out infinite',
              animationDelay: '1.5s',
            }}
          />

          {/* Floating Rectangles */}
          <Box
            sx={{
              position: 'absolute',
              top: '12%',
              left: '22%',
              width: 80,
              height: 80,
              background: 'linear-gradient(135deg, rgba(245,158,11,0.09) 0%, rgba(217,119,6,0.06) 100%)',
              borderRadius: '20px',
              transform: 'rotate(-18deg)',
              animation: 'float 7s ease-in-out infinite',
              animationDelay: '0.3s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '15%',
              right: '18%',
              width: 70,
              height: 70,
              background: 'linear-gradient(135deg, rgba(217,119,6,0.08) 0%, rgba(245,158,11,0.05) 100%)',
              borderRadius: '18px',
              transform: 'rotate(22deg)',
              animation: 'float 6.5s ease-in-out infinite',
              animationDelay: '1.8s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '55%',
              left: '25%',
              width: 65,
              height: 65,
              background: 'linear-gradient(135deg, rgba(245,158,11,0.07) 0%, rgba(217,119,6,0.07) 100%)',
              borderRadius: '16px',
              transform: 'rotate(12deg)',
              animation: 'float 7.8s ease-in-out infinite',
              animationDelay: '2.5s',
            }}
          />

          {/* Small Dots */}
          <Box
            sx={{
              position: 'absolute',
              top: '18%',
              right: '28%',
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'rgba(245, 158, 11, 0.3)',
              animation: 'float 5.5s ease-in-out infinite',
              animationDelay: '0.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '28%',
              left: '20%',
              width: 15,
              height: 15,
              borderRadius: '50%',
              bgcolor: 'rgba(217, 119, 6, 0.28)',
              animation: 'float 6s ease-in-out infinite',
              animationDelay: '1.2s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '45%',
              right: '22%',
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: 'rgba(245, 158, 11, 0.32)',
              animation: 'float 5.8s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '48%',
              left: '12%',
              width: 16,
              height: 16,
              borderRadius: '50%',
              bgcolor: 'rgba(217, 119, 6, 0.25)',
              animation: 'float 6.3s ease-in-out infinite',
              animationDelay: '0.9s',
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="PROFESSIONAL JOURNEY"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 2 }}>
                Work <Box component="span" sx={{ color: 'primary.main' }}>Experience</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontSize: '1rem' }}>
                Shaping minds and building futures through dedicated teaching
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 750, mx: 'auto' }}>
              <Grow in timeout={800}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #fffbf5 100%)',
                    border: '2px solid',
                    borderColor: 'rgba(245, 158, 11, 0.2)',
                    borderRadius: '20px',
                    boxShadow: '0 6px 25px rgba(245, 158, 11, 0.1)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 15px 40px rgba(245, 158, 11, 0.2)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                      {/* Icon Section */}
                      <Grid item xs={12} sm="auto">
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '18px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
                            mx: { xs: 'auto', sm: 0 },
                          }}
                        >
                          <BusinessCenter sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                      </Grid>

                      {/* Content Section */}
                      <Grid item xs={12} sm>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            color: 'secondary.main',
                            fontSize: '1.25rem',
                            lineHeight: 1.3,
                          }}
                        >
                          Assistant Professor of Computer Application
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                          <Chip
                            icon={<School sx={{ fontSize: 16 }} />}
                            label="St. Agnes College"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(245, 158, 11, 0.1)',
                              color: 'primary.dark',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              border: '1px solid',
                              borderColor: 'rgba(245, 158, 11, 0.25)',
                              '& .MuiChip-icon': {
                                color: 'primary.main',
                              },
                            }}
                          />
                          <Chip
                            icon={<LocationOn sx={{ fontSize: 16 }} />}
                            label="Mangalore"
                            size="small"
                            sx={{
                              bgcolor: 'white',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              border: '1px solid',
                              borderColor: 'grey.300',
                              '& .MuiChip-icon': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: 'rgba(245, 158, 11, 0.1)',
                            px: 2,
                            py: 0.8,
                            borderRadius: '10px',
                            border: '1.5px solid',
                            borderColor: 'primary.main',
                            mb: 2,
                          }}
                        >
                          <CalendarToday sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: 'primary.dark',
                              fontSize: '0.9rem',
                            }}
                          >
                            June 2025 – Present
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.7,
                            fontSize: '0.9rem',
                          }}
                        >
                          Teaching professional specializing in lesson delivery, student mentoring, and classroom management, committed to continuous improvement and student success.
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grow>
            </Box>
          </Container>
        </Box>

        {/* Connect Section - REDESIGNED WITH SHAPES (FIXED ALIGNMENT) */}
        <Box id="connect" sx={{ py: 10, bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
          {/* Animated Background Shapes */}

          {/* Large Circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)',
              animation: 'float 9s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px) translateX(0px)' },
                '50%': { transform: 'translateY(-30px) translateX(-20px)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -120,
              left: -120,
              width: 450,
              height: 450,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.08) 0%, transparent 70%)',
              animation: 'float 11s ease-in-out infinite',
              animationDelay: '1.5s',
            }}
          />

          {/* Medium Circles */}
          <Box
            sx={{
              position: 'absolute',
              top: '30%',
              left: '5%',
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11,0.07) 0%, transparent 70%)',
              animation: 'float 8.5s ease-in-out infinite',
              animationDelay: '0.8s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '25%',
              right: '8%',
              width: 280,
              height: 280,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 119, 6, 0.09) 0%, transparent 70%)',
              animation: 'float 10.5s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />

          {/* Morphing Blobs */}
          <Box
            sx={{
              position: 'absolute',
              top: '20%',
              right: '15%',
              width: 180,
              height: 180,
              borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
              background: 'rgba(245, 158, 11, 0.06)',
              animation: 'morph 10s ease-in-out infinite',
              '@keyframes morph': {
                '0%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
                '50%': { borderRadius: '70% 30% 30% 70% / 60% 40% 60% 40%' },
                '100%': { borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '35%',
              left: '12%',
              width: 200,
              height: 200,
              borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
              background: 'rgba(217, 119, 6, 0.05)',
              animation: 'morph 12s ease-in-out infinite',
              animationDelay: '1.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '55%',
              right: '20%',
              width: 150,
              height: 150,
              borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
              background: 'rgba(245, 158, 11, 0.08)',
              animation: 'morph 9s ease-in-out infinite',
              animationDelay: '2.5s',
            }}
          />

          {/* Rotating Squares */}
          <Box
            sx={{
              position: 'absolute',
              top: '15%',
              left: '18%',
              width: 130,
              height: 130,
              border: '3px solid',
              borderColor: 'rgba(245, 158, 11, 0.15)',
              borderRadius: '30px',
              transform: 'rotate(25deg)',
              animation: 'spin 20s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(25deg)' },
                '100%': { transform: 'rotate(385deg)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              right: '12%',
              width: 110,
              height: 110,
              border: '2px solid',
              borderColor: 'rgba(217, 119, 6, 0.12)',
              borderRadius: '26px',
              transform: 'rotate(-20deg)',
              animation: 'spin 18s linear infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '45%',
              left: '8%',
              width: 120,
              height: 120,
              border: '3px solid',
              borderColor: 'rgba(245, 158, 11, 0.18)',
              borderRadius: '28px',
              transform: 'rotate(15deg)',
              animation: 'spin 22s linear infinite',
              animationDelay: '2.5s',
            }}
          />

          {/* Pulsing Rings */}
          <Box
            sx={{
              position: 'absolute',
              top: '35%',
              right: '10%',
              width: 100,
              height: 100,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(245, 158, 11, 0.2)',
              animation: 'pulse-ring 3.8s ease-out infinite',
              '@keyframes pulse-ring': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '100%': {
                  transform: 'scale(2)',
                  opacity: 0,
                },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '30%',
              left: '15%',
              width: 90,
              height: 90,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(217, 119, 6, 0.18)',
              animation: 'pulse-ring 4.2s ease-out infinite',
              animationDelay: '1s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '60%',
              right: '25%',
              width: 95,
              height: 95,
              borderRadius: '50%',
              border: '2px dashed',
              borderColor: 'rgba(245, 158, 11, 0.16)',
              animation: 'pulse-ring 3.5s ease-out infinite',
              animationDelay: '1.8s',
            }}
          />

          {/* Floating Rectangles */}
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              right: '22%',
              width: 75,
              height: 75,
              background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(217,119,6,0.06) 100%)',
              borderRadius: '18px',
              transform: 'rotate(-15deg)',
              animation: 'float 7s ease-in-out infinite',
              animationDelay: '0.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '18%',
              left: '20%',
              width: 65,
              height: 65,
              background: 'linear-gradient(135deg, rgba(217,119,6,0.09) 0%, rgba(245,158,11,0.05) 100%)',
              borderRadius: '16px',
              transform: 'rotate(18deg)',
              animation: 'float 6.5s ease-in-out infinite',
              animationDelay: '1.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: '15%',
              width: 70,
              height: 70,
              background: 'linear-gradient(135deg, rgba(245,158,11,0.07) 0%, rgba(217,119,6,0.08) 100%)',
              borderRadius: '17px',
              transform: 'rotate(10deg)',
              animation: 'float 7.5s ease-in-out infinite',
              animationDelay: '2.2s',
            }}
          />

          {/* Small Dots */}
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              left: '25%',
              width: 16,
              height: 16,
              borderRadius: '50%',
              bgcolor: 'rgba(245, 158, 11, 0.3)',
              animation: 'float 5.5s ease-in-out infinite',
              animationDelay: '0.3s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '40%',
              right: '18%',
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: 'rgba(217, 119, 6, 0.28)',
              animation: 'float 6s ease-in-out infinite',
              animationDelay: '1.3s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '48%',
              left: '22%',
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'rgba(245, 158, 11, 0.32)',
              animation: 'float 5.8s ease-in-out infinite',
              animationDelay: '2.1s',
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Chip
                label="GET IN TOUCH"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                }}
              />
              <Typography variant="h2" sx={{ mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                Let's <Box component="span" sx={{ color: 'primary.main' }}>Connect</Box>
              </Typography>
            </Box>

            {/* CTA Banner */}
            <Grow in timeout={500}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  mb: { xs: 5, md: 7 },
                  background: 'linear-gradient(135deg, #ffffff 0%, #fffbf5 100%)',
                  border: '2px solid',
                  borderColor: 'rgba(245, 158, 11, 0.2)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 3,
                  flexDirection: { xs: 'column', md: 'row' },
                  boxShadow: '0 6px 25px rgba(245, 158, 11, 0.08)',
                  maxWidth: 900,
                  mx: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, textAlign: { xs: 'center', md: 'left' } }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)',
                    }}
                  >
                    <AutoAwesome sx={{ fontSize: 28, color: 'white' }} />
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Interested in collaboration?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Let's discuss opportunities and turn ideas into reality
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, justifyContent: 'center' }}>
                  <Button
                    component={Link}
                    href={socialLinks.email}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: '12px', px: 3 }}
                  >
                    Contact Me
                  </Button>
                  <Button
                    component={Link}
                    href="/SoujanyaCV.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    color="primary"
                    sx={{ borderRadius: '12px', px: 3 }}
                  >
                    View CV
                  </Button>
                </Box>
              </Paper>
            </Grow>

            {/* Contact Cards */}
            <Box>
              <Container maxWidth="md">
                <Stack spacing={3}>
                  {/* Email Card */}
                  <Card
                    component={Link}
                    href={socialLinks.email}
                    sx={{
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2.5,
                      bgcolor: 'white',
                      border: '2px solid',
                      borderColor: 'rgba(234, 67, 53, 0.12)',
                      borderRadius: '16px',
                      boxShadow: 'none',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#EA4335',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(234, 67, 53, 0.12)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        bgcolor: '#EA4335',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Email sx={{ fontSize: 28, color: 'white' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.3 }}>
                        Email Me
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        soujanyaganiga9@gmail.com
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: 'rgba(234, 67, 53, 0.08)',
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ color: '#EA4335', fontSize: '1.2rem' }}>→</Typography>
                    </Box>
                  </Card>

                  {/* WhatsApp Card */}
                  <Card
                    component={Link}
                    href="https://wa.me/919061752785"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2.5,
                      bgcolor: 'white',
                      border: '2px solid',
                      borderColor: 'rgba(37, 211, 102, 0.12)',
                      borderRadius: '16px',
                      boxShadow: 'none',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#25D366',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(37, 211, 102, 0.12)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        bgcolor: '#25D366',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <WhatsApp sx={{ fontSize: 28, color: 'white' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.3 }}>
                        WhatsApp
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: 'rgba(37, 211, 102, 0.08)',
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ color: '#25D366', fontSize: '1.2rem' }}>→</Typography>
                    </Box>
                  </Card>

                  {/* Phone Card */}
                  {/* <Card
                    component={Link}
                    href="tel:+919483121037"
                    sx={{
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2.5,
                      bgcolor: 'white',
                      border: '2px solid',
                      borderColor: 'rgba(59, 130, 246, 0.12)',
                      borderRadius: '16px',
                      boxShadow: 'none',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.12)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        bgcolor: '#3B82F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Phone sx={{ fontSize: 28, color: 'white' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.3 }}>
                        Call Me
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        +91 9483121037
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: 'rgba(59, 130, 246, 0.08)',
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ color: '#3B82F6', fontSize: '1.2rem' }}>→</Typography>
                    </Box>
                  </Card> */}
                </Stack>
              </Container>
            </Box>


          </Container>

        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 8,
            bgcolor: 'secondary.main',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 4 }}>
              {!imageError ? (
                <Avatar
                  src="/profile-photo.jpeg"
                  alt="Soujanya"
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    border: '3px solid',
                    borderColor: 'primary.main',
                  }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                  }}
                >
                  S
                </Avatar>
              )}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Soujanya<Box component="span" sx={{ color: 'primary.main' }}>.</Box>
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
              Dedicated to teaching, research, and academic excellence.            </Typography>


            <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
              <IconButton
                component={Link}
                href={socialLinks.email}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Email />
              </IconButton>
              <IconButton
                component={Link}
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <LinkedIn />
              </IconButton>
              {/* <IconButton
                component={Link}
                href={socialLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <GitHub />
              </IconButton> */}
              {/* <IconButton
                component={Link}
                href={socialLinks.website}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Language />
              </IconButton> */}
            </Box>

            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © 2026 Soujanya Ganiga. All rights reserved.
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}